import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  LayoutDashboard, Users, Settings, BarChart3, MessageSquare,
  LogOut, Shield, ChevronLeft, Menu, X, Home, Pencil
} from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';
import { MinhaConta } from './MinhaConta';

interface SidebarProps {
  clientName?: string;
  /** Papel do usuário NESTE cliente. Indefinido para superadmin. */
  papelNoCliente?: string;
}

export function Sidebar({ clientName, papelNoCliente }: SidebarProps) {
  const { user, logout, isSuperadmin } = useAuth();
  const location = useLocation();
  const { slug } = useParams();
  const [open, setOpen] = useState(false);
  const [editandoConta, setEditandoConta] = useState(false);

  // Configurações expõe token da Meta, autorização do Google e chaves de API.
  // O servidor já barra quem não administra; esconder aqui evita oferecer ao
  // cliente uma porta que só devolveria erro de acesso.
  const administraOCliente = isSuperadmin || papelNoCliente === 'owner' || papelNoCliente === 'manager';

  const clientLinks = slug
    ? [
        { to: `/clients/${slug}/dashboard`, icon: LayoutDashboard, label: 'Dashboard' },
        { to: `/clients/${slug}/leads`, icon: MessageSquare, label: 'Leads' },
        { to: `/clients/${slug}/campaigns`, icon: BarChart3, label: 'Campanhas' },
        ...(administraOCliente
          ? [{ to: `/clients/${slug}/settings`, icon: Settings, label: 'Configurações' }]
          : []),
      ]
    : [];

  const adminLinks = isSuperadmin
    ? [
        { to: '/admin/clients', icon: Shield, label: 'Gerenciar Clientes' },
        { to: '/admin/users', icon: Users, label: 'Usuários' },
      ]
    : [];

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Item da navegação — estilo unificado (rebrand DocAds)
  const itemClasses = (active: boolean) =>
    clsx(
      'flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-lg text-sm transition-colors relative',
      active
        ? 'bg-[var(--surface)] text-[var(--cyan)] border-l-2 border-[var(--cyan)] pl-[10px]'
        : 'text-[var(--text-mid)] hover:text-[var(--text)] hover:bg-[var(--bg)]'
    );

  const nav = (
    <>
      {/* Logo DocAds */}
      <div className="hidden md:block p-4 border-b border-[var(--border)]">
        <Link to="/" className="flex items-center gap-2" aria-label="Ir para página inicial — DocAds">
          <img
            src="/logos/logo-color.webp"
            alt="DocAds"
            className="h-7 w-auto"
            width={96}
            height={28}
          />
        </Link>
      </div>

      {/* Cliente selecionado */}
      {clientName && (
        <div className="px-4 py-3 border-b border-[var(--border)]">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-[var(--text-dim)] hover:text-[var(--cyan)] transition-colors mb-1"
            style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase' }}
          >
            <ChevronLeft size={11} aria-hidden="true" /> Trocar cliente
          </Link>
          <p className="text-sm font-semibold text-[var(--text)] truncate" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {clientName}
          </p>
        </div>
      )}

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {/* Link para home (seleção de clientes) */}
        {!slug && (
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className={itemClasses(location.pathname === '/')}
          >
            <Home size={18} aria-hidden="true" /> Meus Clientes
          </Link>
        )}

        {/* Links do cliente */}
        {clientLinks.length > 0 && (
          <div className="space-y-0.5">
            <p className="ds-micro px-3 mb-2 mt-1">Cliente</p>
            {clientLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={itemClasses(isActive(link.to))}
              >
                <link.icon size={18} aria-hidden="true" />
                {link.label}
              </Link>
            ))}
          </div>
        )}

        {/* Admin */}
        {adminLinks.length > 0 && (
          <div className="mt-6">
            <p className="ds-micro px-3 mb-2">Admin</p>
            {adminLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className={itemClasses(isActive(link.to))}
              >
                <link.icon size={18} aria-hidden="true" />
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </nav>

      {/* Usuário */}
      <div className="p-4 border-t border-[var(--border)]">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
            style={{
              background: 'var(--surface)',
              color: 'var(--cyan)',
              border: '1px solid var(--border)',
              fontFamily: "'JetBrains Mono', monospace",
            }}
            aria-hidden="true"
          >
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[var(--text)] truncate">{user?.name}</p>
            <p className="text-[11px] text-[var(--text-dim)] truncate">{user?.email}</p>
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); setEditandoConta(true); }}
            aria-label="Editar minha conta: e-mail e senha"
            title="Editar e-mail e senha"
            className="ds-toque p-1.5 rounded text-[var(--text-dim)] hover:text-[var(--cyan)] hover:bg-[var(--surface)] transition-colors"
          >
            <Pencil size={14} aria-hidden="true" />
          </button>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 text-sm text-[var(--text-dim)] hover:text-[var(--danger,#f87171)] transition-colors w-full px-2 py-3 md:py-1.5 rounded hover:bg-[var(--surface)]"
        >
          <LogOut size={14} aria-hidden="true" /> Sair
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Barra do celular. O botão solto no canto cobria o título de toda
          página e o "voltar" do detalhe do lead. */}
      <div className="md:hidden fixed top-0 inset-x-0 z-50 h-14 flex items-center gap-2 px-2 bg-[var(--bg-soft)] border-b border-[var(--border)]">
        <button
          onClick={() => setOpen(!open)}
          aria-label={open ? 'Fechar menu de navegação' : 'Abrir menu de navegação'}
          aria-expanded={open}
          aria-controls="primary-navigation"
          className="p-3 rounded-lg text-[var(--text-mid)]"
        >
          {open ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
        </button>
        <Link to="/" onClick={() => setOpen(false)} aria-label="Ir para a página inicial">
          <img src="/logos/logo-color.webp" alt="DocAds" className="h-6 w-6" width={24} height={24} />
        </Link>
        {clientName && <span className="ml-auto pr-2 text-sm text-[var(--text-mid)] truncate">{clientName}</span>}
      </div>

      {/* Mobile overlay */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-40"
          style={{ background: 'rgba(10, 11, 15, 0.8)' }}
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        id="primary-navigation"
        aria-label="Navegação principal"
        className={clsx(
          'fixed md:static top-14 bottom-0 left-0 z-40 w-64 flex flex-col transition-transform md:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
        style={{
          background: 'var(--bg-soft)',
          borderRight: '1px solid var(--border)',
        }}
      >
        {nav}
      </aside>

      {/* Fora do <aside>: o transform dele prenderia o modal fixo dentro do menu. */}
      {editandoConta && user && (
        <MinhaConta emailAtual={user.email} onClose={() => setEditandoConta(false)} />
      )}
    </>
  );
}
